import { useState } from 'react'
import {
  Check,
  LayoutGrid,
  Send,
  Search,
  ChevronRight,
  MoreHorizontal,
  Zap,
  Target,
  Shield,
  Globe,
  BarChart3,
  MessageSquare,
  Brain,
  Users,
  User,
  Briefcase,
  Megaphone,
} from 'lucide-react'
import BrandLogo from '../components/BrandLogo'

/* ═══════════════════════════════════════════════════════════════════════════
   DIZEE — Product Deck
   Two-column layout: copy left, glassmorphism mock UI right. Same branding.
   ═══════════════════════════════════════════════════════════════════════════ */

const DECK_PASSWORD = 'dizee2025'

export default function ProductDeckPage() {
  const [authenticated, setAuthenticated] = useState(false)
  const [password, setPassword] = useState('')
  const [error, setError] = useState(false)

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault()
    if (password === DECK_PASSWORD) {
      setAuthenticated(true)
      setError(false)
    } else {
      setError(true)
    }
  }

  if (!authenticated) {
    return (
      <div className="fixed inset-0 flex items-center justify-center p-8" style={{ backgroundColor: '#13120A' }}>
        <div className="w-full max-w-md">
          <div className="mb-10">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-5 h-5 bg-[#FF2300] rounded-sm flex-shrink-0" />
              <BrandLogo className="h-3 w-auto text-white" />
            </div>
            <p className="text-sm text-zinc-500">Enter the password to view this deck.</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className="block text-sm text-zinc-300 mb-2">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value)
                  setError(false)
                }}
                placeholder="Password"
                autoFocus
                className="w-full rounded-lg border border-white/[0.08] bg-white/[0.03] px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-[#FF2300]/50 focus:border-transparent"
              />
              {error && (
                <p className="mt-2 text-xs text-red-400">Incorrect password. Try again.</p>
              )}
            </div>
            <button
              type="submit"
              className="w-full rounded-lg bg-white px-4 py-3 text-sm font-semibold text-zinc-950 hover:bg-zinc-100 transition-colors"
            >
              View Deck
            </button>
          </form>
          <p className="mt-8 text-[11px] text-zinc-700">
            Confidential — For intended recipients only.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen text-white" style={{ backgroundColor: '#13120A' }}>
      <div className="max-w-[1400px] mx-auto px-8 sm:px-16 lg:px-24">
        {SLIDES.map(({ component: Section }, i) => (
          <section
            key={i}
            className={`py-24 sm:py-32 ${i === 0 ? 'min-h-screen flex flex-col justify-center' : ''} ${i < SLIDES.length - 1 ? 'border-b border-white/[0.04]' : ''}`}
          >
            <Section />
          </section>
        ))}
      </div>
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════════════════════
   LAYOUT & MOCK COMPONENTS
   ═══════════════════════════════════════════════════════════════════════════ */

interface Slide {
  title: string
  component: React.FC
}

function SlideTag({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-block text-[11px] font-semibold text-[#FF2300] uppercase tracking-widest">
      {children}
    </span>
  )
}

/** Two-column slide: left = copy, right = mock UI card(s) */
function SlideLayout({
  tag,
  headline,
  highlight,
  subline,
  bullets,
  roles,
  bottomLine,
  children,
}: {
  tag?: string
  headline: string
  highlight?: string
  subline?: string
  bullets?: string[]
  roles?: string[]
  bottomLine?: string
  children: React.ReactNode
}) {
  return (
    <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
      <div>
        {tag && <SlideTag>{tag}</SlideTag>}
        <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white leading-[1.08]">
          {headline}
          {highlight && <span className="text-[#FF2300] underline decoration-[#FF2300]/60 underline-offset-2">{highlight}</span>}
        </h2>
        {subline && <p className="mt-4 text-base sm:text-lg text-zinc-400">{subline}</p>}
        {bullets && (
          <ul className="mt-6 space-y-3">
            {bullets.map((b, i) => (
              <li key={i} className="flex items-start gap-3">
                <Check className="w-5 h-5 text-[#FF2300] shrink-0 mt-0.5" />
                <span className="text-zinc-300">{b}</span>
              </li>
            ))}
          </ul>
        )}
        {roles && (
          <div className="mt-8 flex flex-wrap items-center gap-2">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="w-9 h-9 rounded-full bg-white/10 border border-white/10" />
            ))}
            <span className="text-zinc-500 mx-1">···</span>
            <div className="flex flex-wrap gap-2">
              {roles.map((r) => (
                <span key={r} className="rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-[13px] text-zinc-300">
                  {r}
                </span>
              ))}
            </div>
          </div>
        )}
        {bottomLine && (
          <p className="mt-6 text-sm text-zinc-500 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-500/80" />
            {bottomLine}
          </p>
        )}
      </div>
      <div className="relative flex items-center justify-center min-h-[320px] lg:min-h-[400px]">
        {children}
      </div>
    </div>
  )
}

/** Glassmorphism mock card with optional glow */
function MockCard({
  className = '',
  glow,
  children,
}: {
  className?: string
  glow?: boolean
  children: React.ReactNode
}) {
  return (
    <div
      className={`rounded-2xl border border-white/10 bg-white/[0.06] backdrop-blur-xl ${glow ? 'shadow-[0_0_60px_-12px_rgba(255,35,0,0.2)]' : ''} ${className}`}
      style={{ boxShadow: glow ? '0 0 60px -12px rgba(255,35,0,0.2)' : undefined }}
    >
      {children}
    </div>
  )
}

/* ── Mock: Hero overlapping windows ──────────────────────────────────────── */
function MockHeroOverlap() {
  return (
    <div className="relative w-full max-w-xl mx-auto flex items-center justify-center">
      <MockCard className="absolute right-0 top-0 w-[72%] max-w-md p-5 opacity-90" glow>
        <div className="flex items-center justify-between mb-4">
          <span className="text-sm font-semibold text-white">Team Management</span>
          <LayoutGrid className="w-4 h-4 text-zinc-500" />
        </div>
        <button className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-[13px] text-zinc-300 mb-4">
          Connect Google <ChevronRight className="w-3.5 h-3.5" />
        </button>
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="rounded-lg bg-white/5 p-3">
            <div className="text-lg font-semibold text-white">42</div>
            <div className="text-[11px] text-zinc-500">Shows</div>
          </div>
          <div className="rounded-lg bg-white/5 p-3">
            <div className="text-lg font-semibold text-white">53</div>
            <div className="text-[11px] text-zinc-500">Tasks</div>
          </div>
        </div>
        <div className="space-y-2">
          {['Charly Manager', 'Promoter'].map((name, i) => (
            <div key={i} className="flex items-center gap-3 py-2 border-b border-white/5 last:border-0">
              <div className="w-8 h-8 rounded-full bg-white/10" />
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-medium text-white truncate">{name}</div>
                <div className="text-[11px] text-zinc-500 truncate">{name.toLowerCase().replace(' ', '')}@spacecamp.la</div>
              </div>
              <span className="text-[10px] text-zinc-500">Manager</span>
            </div>
          ))}
        </div>
      </MockCard>
      <MockCard className="relative left-0 z-10 w-[68%] max-w-sm p-4 shadow-xl" glow>
        <div className="flex gap-4 mb-3 border-b border-white/10 pb-2">
          <span className="text-[13px] font-medium text-[#FF2300] border-b-2 border-[#FF2300] pb-0.5">Shows</span>
          <span className="text-[13px] text-zinc-500">Schedule</span>
        </div>
        <div className="text-[11px] text-zinc-500 space-y-1 mb-3">
          <div>UPCOMING · 31</div>
          <div>SHOWS · 19</div>
          <div>TASKS</div>
        </div>
        <div className="space-y-2">
          {['Charly...', 'charlymanager', 'promoter'].map((label, i) => (
            <div key={i} className="flex items-center gap-2 py-1.5">
              <div className="w-6 h-6 rounded-full bg-white/10" />
              <span className="text-[12px] text-zinc-300 truncate">{label}</span>
            </div>
          ))}
        </div>
      </MockCard>
      <MockCard className="absolute right-4 bottom-0 w-[58%] max-w-xs p-4 z-20" glow>
        <div className="flex items-center gap-2 mb-3 rounded-lg bg-white/5 border border-white/10 px-2 py-1.5">
          <Search className="w-3.5 h-3.5 text-zinc-500" />
          <span className="text-[12px] text-zinc-500">Ask anything...</span>
        </div>
        <div className="space-y-1.5">
          {['Shrine – 7,842 / 10,500 (75% sold)', 'Crew lands 5:30 PM', '+12 Playlists added', 'Final splits uploaded'].map((t, i) => (
            <div key={i} className="flex items-center gap-2 text-[12px] text-zinc-300">
              <div className="w-4 h-4 rounded-full bg-emerald-500/30 flex items-center justify-center flex-shrink-0">
                <Check className="w-2.5 h-2.5 text-emerald-400" />
              </div>
              {t}
            </div>
          ))}
        </div>
        <div className="mt-3 flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-2 py-1.5">
          <span className="text-[11px] text-zinc-500 flex-1">Ask Dizee...</span>
          <Send className="w-3.5 h-3.5 text-[#FF2300]" />
        </div>
      </MockCard>
    </div>
  )
}

/* ── Mock: Update Ticket Counts ─────────────────────────────────────────── */
function MockTicketCounts() {
  const rows = [
    { name: 'Tempo', loc: 'Philadelphia · Mar 20, 2026', cap: '400', count: '328', pct: 82, status: 'On Sale', saved: true },
    { name: 'Stage West', loc: 'State College · Mar 21, 2026', cap: '1,200', count: '915', pct: 76, status: 'Venue TBA', saved: true },
    { name: 'Pal Norte', loc: 'Monterrey · Mar 27, 2026', cap: 'TBA', count: '–', pct: 0, status: 'Capacity TBA', saved: false },
  ]
  return (
    <MockCard className="w-full max-w-lg p-5" glow>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-base font-semibold text-white">Update Ticket Counts</h3>
          <p className="text-[13px] text-zinc-500">19 Upcoming Shows</p>
        </div>
        <div className="flex gap-2">
          <button className="rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-[12px] text-zinc-400">Import</button>
          <button className="rounded-lg bg-[#FF2300] px-3 py-1.5 text-[12px] font-medium text-white">Save All</button>
        </div>
      </div>
      <div className="space-y-3">
        {rows.map((r, i) => (
          <div key={i} className="rounded-xl border border-white/10 bg-white/[0.04] p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-white">{r.name}</span>
              <div className="flex items-center gap-2">
                {r.saved && <span className="text-[11px] text-emerald-500">✔ Saved</span>}
                {!r.saved && <span className="text-[11px] text-zinc-500">• Pending</span>}
                <span className="text-[10px] text-zinc-500 border border-white/10 rounded px-1.5">{r.status}</span>
              </div>
            </div>
            <p className="text-[11px] text-zinc-500 mb-2">{r.loc}</p>
            <div className="grid grid-cols-3 gap-2 text-[11px]">
              <div><span className="text-zinc-500">Sellable Cap</span><div className="font-medium text-white">{r.cap}</div></div>
              <div><span className="text-zinc-500">Ticket Count</span><div className="font-medium text-white">{r.count}</div></div>
              <div><span className="text-zinc-500">% Sold</span><div className="font-medium text-[#FF2300]">{r.pct ? `${r.pct}%` : '–'}</div></div>
            </div>
            {r.pct > 0 && (
              <div className="mt-2 h-1.5 rounded-full bg-white/10 overflow-hidden">
                <div className="h-full rounded-full bg-[#FF2300]" style={{ width: `${r.pct}%` }} />
              </div>
            )}
          </div>
        ))}
      </div>
    </MockCard>
  )
}

/* ── Mock: Know What Needs Attention ────────────────────────────────────── */
function MockUpdateTickets() {
  const items = [
    { title: 'Tempo', warn: true, sub: 'Slowing down with 5 days left', cap: '400', count: '298', pct: 75 },
    { title: 'Stage West', sub: 'State College - Mar 21, 2026', cap: '1,200', count: '571', pct: 47, synced: true },
    { title: 'Pal Norte', sub: 'Monterrey - Mar 27, 2026', cap: 'TBA', count: '–', pct: 0, pending: true },
  ]
  return (
    <MockCard className="w-full max-w-lg p-5" glow>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-base font-semibold text-white">Update Tickets</h3>
          <p className="text-[13px] text-zinc-500">19 upcoming Shows</p>
        </div>
        <div className="flex gap-2">
          <button className="rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-[12px] text-zinc-400">Import</button>
          <button className="rounded-lg bg-[#FF2300] px-3 py-1.5 text-[12px] font-medium text-white">Save All</button>
        </div>
      </div>
      <div className="space-y-3">
        {items.map((r, i) => (
          <div key={i} className="rounded-xl border border-white/10 bg-white/[0.04] p-3">
            <div className="flex items-center justify-between gap-2 mb-1">
              <span className="text-sm font-medium text-white">{r.title}</span>
              {r.warn && <span className="text-[10px] text-amber-400 flex items-center gap-1">⚠ Warning</span>}
              {r.synced && <span className="text-[10px] text-emerald-500">✔ Synced</span>}
              {r.pending && <span className="text-[10px] text-zinc-500">• Pending</span>}
            </div>
            {r.sub && <p className="text-[11px] text-zinc-500 mb-2">{r.sub}</p>}
            <div className="grid grid-cols-3 gap-2 text-[11px]">
              <div><span className="text-zinc-500">Sellable Cap</span><div className="font-medium text-white">{r.cap}</div></div>
              <div><span className="text-zinc-500">Ticket Count</span><div className="font-medium text-white">{r.count}</div></div>
              <div><span className="text-zinc-500">% Sold</span><div className="font-medium text-[#FF2300]">{r.pct ? `${r.pct}%` : '–'}</div></div>
            </div>
            {r.pct > 0 && (
              <div className="mt-2 h-1.5 rounded-full bg-white/10 overflow-hidden">
                <div className="h-full rounded-full bg-[#FF2300]" style={{ width: `${r.pct}%` }} />
              </div>
            )}
          </div>
        ))}
      </div>
    </MockCard>
  )
}

/* ── Mock: Team tab ─────────────────────────────────────────────────────── */
function MockTeamTab() {
  const members = [
    { name: 'Charly', email: 'charly@spacecamp.la', role: 'Owner' },
    { name: 'charlymanager', email: 'charlyteam@spacecamp.la', role: 'Manager' },
    { name: 'promoter', email: 'promoter@spacecamp.la', role: 'Manager' },
  ]
  return (
    <MockCard className="w-full max-w-md p-5" glow>
      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-4 border-b border-white/10">
          <span className="text-[13px] text-zinc-500 pb-2">Schedule</span>
          <span className="text-[13px] text-zinc-500 pb-2">Shows</span>
          <span className="text-[13px] font-medium text-white border-b-2 border-[#FF2300] pb-2">Team</span>
        </div>
        <button className="rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-[12px] text-zinc-400">Invite Team Member</button>
      </div>
      <div className="space-y-0 divide-y divide-white/5">
        {members.map((m, i) => (
          <div key={i} className="flex items-center gap-3 py-3">
            <div className="w-10 h-10 rounded-full bg-white/10 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-[14px] font-medium text-white">{m.name}</div>
              <div className="text-[12px] text-zinc-500">{m.email}</div>
            </div>
            <div className="flex items-center gap-1.5 text-[12px] text-zinc-500">
              <span className="w-2 h-2 rounded-full bg-emerald-500/80" />
              {m.role}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2">
        <span className="text-[13px] text-zinc-500 flex-1">How&apos;d the show in Indy sell?</span>
        <button className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center"><Send className="w-4 h-4 text-[#FF2300]" /></button>
      </div>
    </MockCard>
  )
}

/* ── Mock: Manage Tasks ────────────────────────────────────────────────── */
function MockManageTasks() {
  const tasks = [
    { tag: 'BRAND DEAL', title: 'Draft IG Story for Genius promotion', due: 'Due Tomorrow', status: 'In progress' },
    { tag: 'DSP PITCH', title: 'Pitch single to Spotify editors', due: 'Due Friday', status: 'Tomorrow' },
    { tag: 'MARKETING', title: 'Approve artwork v3', due: 'Apr 9, 2026', status: 'Not started' },
  ]
  return (
    <MockCard className="w-full max-w-lg p-5" glow>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-base font-semibold text-white">Manage Tasks</h3>
          <p className="text-[13px] text-zinc-500">6 Total Tasks</p>
        </div>
        <button className="rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-[12px] text-zinc-400 flex items-center gap-1">Add Tasks ▾</button>
      </div>
      <div className="flex gap-2 mb-3 border-b border-white/10">
        {['ALL', 'EDIT', 'DSP', 'BRAND'].map((t, i) => (
          <span key={t} className={`text-[12px] pb-2 ${i === 3 ? 'text-white font-medium border-b-2 border-[#FF2300]' : 'text-zinc-500'}`}>{t}</span>
        ))}
      </div>
      <div className="rounded-lg border border-white/10 bg-white/5 px-2 py-1.5 flex items-center gap-2 mb-3">
        <Search className="w-3.5 h-3.5 text-zinc-500" />
        <span className="text-[12px] text-zinc-500">Filter by keyword...</span>
      </div>
      <div className="space-y-2">
        {tasks.map((t, i) => (
          <div key={i} className="rounded-xl border border-white/10 bg-white/[0.04] p-3 flex items-center gap-3">
            <span className="text-[10px] font-medium bg-[#FF2300]/20 text-[#FF2300] px-2 py-0.5 rounded">{t.tag}</span>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-medium text-white">{t.title}</div>
              <div className="text-[11px] text-zinc-500">{t.due}</div>
            </div>
            <div className="w-8 h-8 rounded-full bg-white/10 flex-shrink-0" />
            <span className="text-[11px] text-zinc-500 flex items-center gap-1">
              {t.status === 'In progress' && <span className="w-1.5 h-1.5 rounded-full bg-[#FF2300]" />}
              {t.status}
            </span>
            <MoreHorizontal className="w-4 h-4 text-zinc-500 flex-shrink-0" />
          </div>
        ))}
      </div>
    </MockCard>
  )
}

/* ── Mock: Ask Dizee / Key Insights ─────────────────────────────────────── */
function MockAskDizee() {
  return (
    <MockCard className="w-full max-w-md p-5" glow>
      <h3 className="text-base font-semibold text-white mb-3">Ask Dizee.</h3>
      <div className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 flex items-center gap-2 mb-4">
        <Search className="w-4 h-4 text-zinc-500" />
        <span className="text-[13px] text-zinc-500 flex-1">Ask anything...</span>
        <Send className="w-4 h-4 text-[#FF2300]" />
      </div>
      <div className="space-y-3">
        <div className="flex items-start gap-2">
          <div className="w-5 h-5 rounded-full bg-emerald-500/30 flex items-center justify-center flex-shrink-0 mt-0.5"><Check className="w-3 h-3 text-emerald-400" /></div>
          <div><div className="text-[13px] text-white">Shrine - 7,842 / 10,500 (75% sold)</div></div>
        </div>
        <div className="flex items-start gap-2">
          <div className="w-5 h-5 rounded-full bg-emerald-500/30 flex items-center justify-center flex-shrink-0 mt-0.5"><Check className="w-3 h-3 text-emerald-400" /></div>
          <div>
            <div className="text-[13px] text-white">Today&apos;s top three opening Spotify playlists</div>
            <div className="flex gap-1 mt-1">
              <span className="w-2 h-2 rounded-sm bg-[#FF2300]" /><span className="text-[11px] text-zinc-500">Mellow Mood</span>
              <span className="w-2 h-2 rounded-sm bg-amber-600" /><span className="text-[11px] text-zinc-500">Lo-Fi Beats</span>
              <span className="w-2 h-2 rounded-sm bg-zinc-600" /><span className="text-[11px] text-zinc-500">Young & Free</span>
            </div>
          </div>
        </div>
        <div className="flex items-start gap-2">
          <div className="w-5 h-5 rounded-full bg-emerald-500/30 flex items-center justify-center flex-shrink-0 mt-0.5"><span className="text-[10px] font-semibold text-emerald-400">$</span></div>
          <div><div className="text-[13px] text-white font-semibold">$1,235.89 estimated weekly</div><div className="text-[11px] text-zinc-500">Royalties Sunday</div></div>
        </div>
        <div className="flex items-start gap-2">
          <div className="w-5 h-5 rounded-full bg-emerald-500/30 flex items-center justify-center flex-shrink-0 mt-0.5"><span className="text-[10px] font-semibold text-emerald-400">S</span></div>
          <div><div className="text-[13px] text-white">Nashville: Thursday flights, first show Friday</div><div className="text-[11px] text-zinc-500">Due Apr 13, 2026</div></div>
        </div>
      </div>
      <div className="mt-4 pt-3 border-t border-white/10 rounded-lg border border-white/10 bg-white/5 px-3 py-2 flex items-center gap-2">
        <span className="text-[13px] text-zinc-500 flex-1">How&apos;d the show in Indy sell?</span>
        <Send className="w-4 h-4 text-[#FF2300]" />
      </div>
    </MockCard>
  )
}

/* ── Mock: Schedule / Calendar ───────────────────────────────────────────── */
function MockSchedule() {
  return (
    <MockCard className="w-full max-w-md p-5" glow>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-white">Schedule</h3>
        <button className="flex items-center gap-1 rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-[12px] text-zinc-400">Connect Google <ChevronRight className="w-3.5 h-3.5" /></button>
      </div>
      <div className="flex gap-4 mb-4">
        <div className="text-3xl font-semibold text-white">31</div>
        <div className="text-3xl font-semibold text-white">19</div>
        <div className="text-3xl font-semibold text-zinc-500">12</div>
      </div>
      <div className="space-y-2 text-[13px]">
        <div className="text-zinc-400">LA to NY flights (Charly to approve)</div>
        <div className="text-zinc-400">Call Haley Monday</div>
        <div className="text-zinc-400">Task Reley Monday</div>
      </div>
    </MockCard>
  )
}

/* ═══════════════════════════════════════════════════════════════════════════
   SHARED HELPERS (ported from investor deck)
   ═══════════════════════════════════════════════════════════════════════════ */

const DECK_IMAGES = {
  problem: '/deck/musicwork.png',
  whyWins: '/deck/party.png',
}

function DizeeWord({ className = '' }: { className?: string }) {
  return <span className={className || undefined}>DIZEE</span>
}

function TwoCol({
  tag,
  tagNode,
  headline,
  headlineNode,
  highlight,
  subline,
  children,
  right,
}: {
  tag?: string
  tagNode?: React.ReactNode
  headline?: string
  headlineNode?: React.ReactNode
  highlight?: string
  subline?: string
  children?: React.ReactNode
  right: React.ReactNode
}) {
  return (
    <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center min-w-0">
      <div className="min-w-0">
        {(tagNode ?? tag) && <SlideTag>{tagNode ?? tag}</SlideTag>}
        <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white leading-[1.08] break-words">
          {headlineNode ?? (<>{(headline ?? '')}{highlight && <span className="text-[#FF2300]">{highlight}</span>}</>)}
        </h2>
        {subline && <p className="mt-4 text-base sm:text-lg text-zinc-400 break-words">{subline}</p>}
        <div className="min-w-0">{children}</div>
      </div>
      <div className="flex items-center justify-center min-h-[280px] lg:min-h-[340px] min-w-0">
        {right}
      </div>
    </div>
  )
}

const PROBLEM_LOGOS: { label: string; src: string }[] = [
  { label: 'iMessage', src: '/logos/deck/apple.svg' },
  { label: 'Asana', src: '/logos/deck/asana.svg' },
  { label: 'ASCAP', src: '/logos/deck/ascap.svg' },
  { label: 'Gmail', src: '/logos/deck/gmail.svg' },
  { label: 'Google Sheets', src: '/logos/deck/googlesheets.svg' },
  { label: 'MasterTour', src: '/logos/deck/mastertour.svg' },
  { label: 'Slack', src: '/logos/deck/slack.svg' },
  { label: 'Spotify', src: '/logos/deck/spotify.svg' },
]

function MockFragmentedLogos() {
  const items: {
    left: string
    top: string
    rotation?: number
    opacity?: number
    translateX?: number
    translateY?: number
    zIndex?: number
  }[] = [
    { left: '24%', top: '6%', rotation: -5, opacity: 0.94, translateX: 4, translateY: -2, zIndex: 10 },
    { left: '68%', top: '8%', rotation: 4, opacity: 1, translateX: -3, translateY: 5, zIndex: 8 },
    { left: '75%', top: '38%', rotation: -2, opacity: 0.9, translateX: 6, translateY: 2, zIndex: 12 },
    { left: '22%', top: '32%', rotation: 3, opacity: 0.97, translateX: -5, translateY: -4, zIndex: 9 },
    { left: '44%', top: '26%', rotation: -4, opacity: 0.92, translateX: 2, translateY: 6, zIndex: 11 },
    { left: '14%', top: '58%', rotation: 2, opacity: 0.98, translateX: -2, translateY: -3, zIndex: 13 },
    { left: '48%', top: '62%', rotation: -3, opacity: 0.93, translateX: 5, translateY: 1, zIndex: 7 },
    { left: '72%', top: '68%', rotation: 5, opacity: 0.96, translateX: -4, translateY: 4, zIndex: 14 },
  ]
  return (
    <div className="relative w-full max-w-lg h-[280px] sm:h-[320px] md:h-[380px]">
      {PROBLEM_LOGOS.map((logo, i) => {
        const item = items[i]
        const tx = item.translateX ?? 0
        const ty = item.translateY ?? 0
        return (
          <div
            key={i}
            className="absolute flex flex-col items-center gap-2"
            style={{
              left: item.left,
              top: item.top,
              zIndex: item.zIndex ?? 10,
              opacity: item.opacity ?? 1,
              transform: `translate(${tx}px, ${ty}px) rotate(${item.rotation ?? 0}deg)`,
            }}
          >
            <img src={logo.src} alt={logo.label} className="h-8 w-auto object-contain sm:h-12" />
            <span className="text-[10px] sm:text-[12px] text-zinc-500 whitespace-nowrap">{logo.label}</span>
          </div>
        )
      })}
    </div>
  )
}

const SOLUTION_ITEMS: (
  | { type: 'logo'; label: string; src: string }
  | { type: 'role'; label: string; Icon: React.ComponentType<{ className?: string }> }
)[] = [
  ...PROBLEM_LOGOS.map((l) => ({ type: 'logo' as const, label: l.label, src: l.src })),
  { type: 'role', label: 'Artist', Icon: User },
  { type: 'role', label: 'Agent', Icon: Briefcase },
  { type: 'role', label: 'Promoter', Icon: Megaphone },
  { type: 'role', label: 'Managers', Icon: Users },
]

function MockCentralDizzy() {
  const radius = 38
  const positions = [0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330].map((deg) => {
    const rad = (deg * Math.PI) / 180
    const x = 50 + radius * Math.sin(rad)
    const y = 50 - radius * Math.cos(rad)
    return { x, y, left: `${x.toFixed(1)}%`, top: `${y.toFixed(1)}%` }
  })

  return (
    <div className="relative w-full max-w-lg flex flex-col items-center">
      <div className="relative w-full aspect-square max-h-[240px] sm:max-h-[280px] md:max-h-[320px] mx-auto">
        {SOLUTION_ITEMS.map((item, i) => (
          <div
            key={i}
            className="absolute flex flex-col items-center justify-center gap-1.5 z-10 -translate-x-1/2 -translate-y-1/2"
            style={{ left: positions[i].left, top: positions[i].top }}
          >
            {item.type === 'logo' ? (
              <img src={item.src} alt={item.label} className="h-7 w-auto object-contain sm:h-10" />
            ) : (
              <div className="h-7 w-7 sm:h-10 sm:w-10 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-zinc-400 shrink-0">
                <item.Icon className="w-3.5 h-3.5 sm:w-5 sm:h-5" />
              </div>
            )}
            <span className="text-[9px] sm:text-[11px] text-zinc-500 whitespace-nowrap">{item.label}</span>
          </div>
        ))}
        <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden>
          {positions.map((pos, i) => (
            <line
              key={i}
              x1={pos.x}
              y1={pos.y}
              x2={50}
              y2={50}
              stroke="rgba(255,255,255,0.2)"
              strokeWidth="0.35"
              strokeDasharray="1.5 1"
            />
          ))}
        </svg>
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center gap-1.5 z-10">
          <div className="w-7 h-7 bg-[#FF2300] rounded-sm flex-shrink-0" />
          <BrandLogo className="h-5 w-auto text-white" />
        </div>
      </div>
    </div>
  )
}

const TEAM_MEMBERS = [
  {
    name: 'Ryan Thomas',
    role: 'CEO',
    image: '/deck/ryan.png',
    points: [
      '15+ years in the dance music industry',
      'Managed Grammy-nominated and platinum-selling artists',
      'Deep global relationships with agencies, promoters, and labels',
    ],
  },
  {
    name: 'Edward Contreras',
    role: 'CTO',
    image: '/deck/edward.png',
    points: [
      'Technologist, product builder, and entrepreneur',
      'Expertise in AI systems, music technology, and multi-product ecosystems',
      <>Architect behind <DizeeWord />'s platform and integration layer</>,
    ],
  },
  {
    name: 'Aaron P. Wong',
    role: 'Board member and Strategic Advisor',
    image: '/deck/aaron.png',
    points: [
      'Founder of Super73, a globally recognized electric bike company',
      'Experienced in scaling hardware businesses and social presence',
      'Raised and closed multiple rounds of venture funding',
    ],
  },
  {
    name: 'Owais Ghaus',
    role: 'CMO',
    image: '/deck/owais.png',
    points: [
      'Artist, marketer, and technologist',
      'Music executive with deep industry connections',
      'Specializes in content strategy, creator marketing, and artist development',
    ],
  },
  {
    name: 'Christopher Alba',
    role: 'CBO',
    image: '/deck/christopher.png',
    points: [
      'Pioneer in the dance music industry with deep roots in creative direction and brand strategy',
      'Creative director for hundreds of artists across the music industry',
      'Chief Brand Officer — leads creative direction, brand identity, and visual storytelling for DIZEE',
    ],
  },
]

function TeamCard({ t }: { t: (typeof TEAM_MEMBERS)[0] }) {
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-6 sm:p-7 flex flex-col min-h-[220px]">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full overflow-hidden border border-white/[0.08] flex-shrink-0 bg-white/5">
          <img src={t.image} alt={t.name} className="w-full h-full object-cover" />
        </div>
        <div className="min-w-0">
          <h3 className="text-[15px] sm:text-[16px] font-semibold text-white tracking-tight">{t.name}</h3>
          <p className="text-[12px] text-[#FF2300] font-medium mt-0.5">{t.role}</p>
        </div>
      </div>
      <ul className="space-y-3 flex-1">
        {t.points.map((point, j) => (
          <li key={j} className="flex items-start gap-2">
            <span className="mt-[0.55em] h-1.5 w-1.5 rounded-full bg-zinc-500 shrink-0" aria-hidden />
            <p className="text-[15px] text-zinc-400 leading-relaxed flex-1 min-w-0 pt-0">{point}</p>
          </li>
        ))}
      </ul>
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════════════════════
   SLIDES
   ═══════════════════════════════════════════════════════════════════════════ */

function SlideTitle() {
  return (
    <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
      <div>
        <div className="flex items-center gap-2.5 mb-8">
          <div className="w-6 h-6 bg-[#FF2300] rounded-sm flex-shrink-0" />
          <BrandLogo className="h-4 w-auto text-white" />
        </div>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white leading-[1.08] max-w-xl">
          Dizee makes it easy to manage a <span className="text-[#FF2300]">music career.</span>
        </h1>
        <p className="text-base sm:text-lg text-zinc-400 mt-6">Focus on making music. Leave the rest to us.</p>
        <div className="mt-8 flex flex-wrap items-center gap-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="w-10 h-10 rounded-full bg-white/10 border border-white/10" />
          ))}
          <span className="text-zinc-500 mx-1">···</span>
          <p className="text-[13px] text-zinc-500">Manager · Booking Agent · Artist · Promoter</p>
        </div>
        <div className="mt-10 flex gap-3">
          <a href="#problems" className="rounded-lg bg-[#FF2300] px-5 py-2.5 text-sm font-medium text-white hover:opacity-90 transition-opacity">Learn more</a>
          <a href="#problems" className="rounded-lg border border-[#FF2300] px-5 py-2.5 text-sm font-medium text-white hover:bg-[#FF2300]/10 transition-colors">Get started</a>
        </div>
      </div>
      <div className="relative flex items-center justify-center min-h-[340px] lg:min-h-[400px]">
        <MockHeroOverlap />
      </div>
    </div>
  )
}

function SlideProblems() {
  return (
    <SlideLayout
      tag="Problems we solve"
      headline="Communication and data that actually stay "
      highlight="in sync."
      subline="One source of truth. One place to look. No more version chaos."
      bullets={[
        'Seamless communication between internal and external teams (promoters, agents, labels).',
        'Ticket counts that stay current everywhere — never work from stale numbers.',
      ]}
      bottomLine="Live data across team"
    >
      <MockTicketCounts />
    </SlideLayout>
  )
}

function SlideSheets() {
  return (
    <SlideLayout
      tag="Google Sheets integration"
      headline="Your show data, bidirectional sync. "
      highlight="No workflow change."
      subline="We meet you where you already are. Connect once, and show data and ticket counts flow between Dizee and Google Sheets automatically."
      bullets={[
        'One sheet, many tabs — ticket tab syncs across everything.',
        '~2 minutes to set up. Bidirectional sync, no duplicate entry.',
      ]}
      bottomLine="Single glance, every morning."
    >
      <MockUpdateTickets />
    </SlideLayout>
  )
}

function SlideAccess() {
  return (
    <SlideLayout
      tag="Teams & access"
      headline="Everyone sees only "
      highlight="what they need."
      subline="Granular access so your team has a separate view for the things they work on only. Less noise, fewer mistakes."
      bullets={[
        'Shows only — tour dates, ticket counts, promoters.',
        'Music only — releases, catalog, credits.',
        'Full access — managers and admins see the full career view.',
      ]}
      roles={['Manager', 'Booking Agent', 'Artist', 'Promoter']}
      bottomLine="Everyone sees the same thing."
    >
      <MockTeamTab />
    </SlideLayout>
  )
}

function SlideCalendar() {
  return (
    <SlideLayout
      tag="Schedule & Google Calendar"
      headline="Your schedule, integrated with "
      highlight="Google Calendar."
      subline="Add items from Dizee into your Google Calendar, create a shared calendar, or pick specific calendars. See everything in one place."
      bullets={[
        'Add show releases, music releases, and tasks from Dizee to Google Calendar.',
        'Shared calendar for the whole team, or pick specific calendars.',
        'Toggle between “what’s on Google Calendar” and “everything on the artist’s career”.',
      ]}
      bottomLine="One schedule view: Dizee + Google — all in sync."
    >
      <MockSchedule />
    </SlideLayout>
  )
}

function SlideTasks() {
  return (
    <SlideLayout
      tag="Tasks"
      headline="Never miss key "
      highlight="deadlines."
      subline="Need your team to handle marketing tasks? Managers track it all here."
      bullets={[
        'Create and assign tasks.',
        'Approve brand deals.',
        'Stay ahead of pitch deadlines.',
      ]}
      bottomLine="Everyone knows what to do next."
    >
      <MockManageTasks />
    </SlideLayout>
  )
}

function SlideSummary() {
  return (
    <SlideLayout
      tag="Summary"
      headline="Dizee becomes the "
      highlight="source of truth"
      subline="Ask questions about: Shows, Music, Publishing, Travel, Marketing, Team. From asking your team to asking Dizee."
      bullets={[
        'Get key insights instantly — ticket sales, promo opportunities, royalties, travel.',
        'Answers for any question, in one place.',
      ]}
    >
      <MockAskDizee />
    </SlideLayout>
  )
}

function SlideMission() {
  return (
    <div className="flex flex-col justify-center">
      <SlideTag>Mission</SlideTag>
      <h2 className="mt-8 text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tighter text-white leading-[1.25] max-w-4xl">
        <span className="block whitespace-normal sm:whitespace-nowrap">&quot;To unify every part of a music career within <span className="text-[#FF2300]">one intelligent system</span>,</span>
        <span className="block"><span className="whitespace-normal sm:whitespace-nowrap">enabling artists and their teams to move faster, make smarter decisions</span>,</span>
        <span className="block">and stay aligned around <span className="text-[#FF2300] whitespace-normal sm:whitespace-nowrap">a single source of truth.</span>&quot;</span>
      </h2>
    </div>
  )
}

function SlideCurrentState() {
  return (
    <TwoCol
      tag="Current State"
      headline="The music business is still "
      highlight="outdated."
      subline="No unified system to manage the modern music business."
      right={<MockFragmentedLogos />}
    >
      <div className="mt-10 grid sm:grid-cols-2 gap-5 min-w-0">
        <div className="rounded-xl border border-white/[0.08] bg-white/[0.03] p-6 min-w-0">
          <div className="flex items-center gap-2 mb-2"><Zap className="w-4 h-4 text-[#FF2300] shrink-0" /><span className="text-sm font-semibold text-[#FF2300] break-words">Manual Processes</span></div>
          <p className="text-base text-zinc-300 leading-relaxed break-words">Nothing automated or connected.</p>
        </div>
        <div className="rounded-xl border border-white/[0.08] bg-white/[0.03] p-6 min-w-0">
          <div className="flex items-center gap-2 mb-2"><MessageSquare className="w-4 h-4 text-[#FF2300] shrink-0" /><span className="text-sm font-semibold text-[#FF2300] break-words">Fragmented Communication</span></div>
          <p className="text-base text-zinc-300 leading-relaxed break-words">Spreadsheets, texts, email — critical decisions in iMessage nobody can search.</p>
        </div>
        <div className="rounded-xl border border-white/[0.08] bg-white/[0.03] p-6 min-w-0">
          <div className="flex items-center gap-2 mb-2"><Shield className="w-4 h-4 text-[#FF2300] shrink-0" /><span className="text-sm font-semibold text-[#FF2300] break-words">Legacy Tools</span></div>
          <p className="text-base text-zinc-300 leading-relaxed break-words">Built for a different era. Can&apos;t keep up.</p>
        </div>
        <div className="rounded-xl border border-white/[0.08] bg-white/[0.03] p-6 min-w-0">
          <div className="flex items-center gap-2 mb-2"><Globe className="w-4 h-4 text-[#FF2300] shrink-0" /><span className="text-sm font-semibold text-[#FF2300] break-words">Disconnected Systems</span></div>
          <p className="text-base text-zinc-300 leading-relaxed break-words">Data in a dozen platforms. No way to connect the dots.</p>
        </div>
      </div>
    </TwoCol>
  )
}

function SlideProblemStatement() {
  return (
    <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center min-w-0">
      <div className="flex items-center justify-center min-h-[280px] lg:min-h-[340px] w-full order-2 lg:order-1">
        <div className="relative w-full max-w-lg rounded-2xl overflow-hidden border border-white/[0.08] shadow-xl aspect-[16/10] bg-white/[0.03]">
          <img
            src={DECK_IMAGES.problem}
            alt="Music work"
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
        </div>
      </div>
      <div className="min-w-0 order-1 lg:order-2">
        <SlideTag>The Problem</SlideTag>
        <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white leading-[1.15]">
          Artists lack clear <span className="text-[#FF2300] whitespace-normal sm:whitespace-nowrap">real-time visibility</span> into what&apos;s happening in their career.
        </h2>
        <div className="mt-10 max-w-3xl space-y-5">
          <div>
            <p className="font-semibold text-[#FF2300] mb-1">No Centralized System:</p>
            <p className="text-base text-zinc-300 leading-relaxed">Fragmented tools and data force music teams to make critical revenue decisions without the full picture.</p>
          </div>
          <div>
            <p className="font-semibold text-[#FF2300] mb-1">Limited Access to Fast, Reliable Data:</p>
            <p className="text-base text-zinc-300 leading-relaxed">Artist teams lack timely, reliable access to the info needed to act quickly, prevent risks or capture valuable opportunities.</p>
          </div>
          <div>
            <p className="font-semibold text-[#FF2300] mb-1">Missed Opportunities:</p>
            <p className="text-base text-zinc-300 leading-relaxed">By the time data is gathered and verified, opportunities are often missed and communication breaks down.</p>
          </div>
          <div>
            <p className="font-semibold text-[#FF2300] mb-2">This Results In:</p>
            <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-base text-zinc-300 leading-relaxed">
              <div className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-[#FF2300] shrink-0" />Lost Revenue</div>
              <div className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-[#FF2300] shrink-0" />Slowed Growth</div>
              <div className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-[#FF2300] shrink-0" />Missed Opportunities</div>
              <div className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-[#FF2300] shrink-0" />Communication Breakdowns</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function SlideRealConversations() {
  return (
    <div>
      <SlideTag>Real Conversations</SlideTag>
      <h2 className="mt-3 text-2xl sm:text-3xl font-semibold tracking-tighter text-white">
        Before vs after <DizeeWord className="text-[#FF2300]" />.
      </h2>
      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-8 w-full max-w-none">
        <div className="min-h-[320px] w-full">
          <p className="text-[11px] font-semibold text-zinc-400 uppercase tracking-wider mb-2">Before <DizeeWord className="text-[#FF2300]" /> — messages scattered</p>
          <img src="/deck/conversations.png" alt="Before and after DIZEE conversations" className="w-full h-auto object-contain" />
        </div>
        <div className="space-y-4">
          <p className="text-[11px] font-semibold text-zinc-400 uppercase tracking-wider">After <DizeeWord className="text-[#FF2300]" /></p>
          <div className="rounded-xl border border-white/[0.08] bg-white/[0.03] p-4 space-y-4">
            <div>
              <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Ticket count</p>
              <p className="text-xl font-semibold text-white">New York: Brooklyn Storehouse</p>
              <p className="text-sm text-zinc-400 mt-0.5">5,000 capacity, 2,435 sold, 48.7% sold</p>
              <div className="mt-2 h-2 rounded-full bg-white/10 overflow-hidden">
                <div className="h-full rounded-full bg-[#FF2300] w-[48.7%]" />
              </div>
              <p className="text-[12px] text-[#FF2300] mt-1">48.7% sold — updated live</p>
            </div>
            <div className="border-t border-white/10 pt-3">
              <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-2">Schedule</p>
              <div className="space-y-2">
                <div className="flex items-center justify-between rounded-lg bg-white/5 border border-white/10 px-3 py-2">
                  <span className="text-[12px] text-white">Brooklyn Storehouse NY</span>
                </div>
                <div className="flex items-center gap-2 text-base text-zinc-300">
                  <span>LA → Chicago → NY</span>
                </div>
              </div>
            </div>
            <div className="border-t border-white/10 pt-3">
              <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-2">Music · Streams</p>
              <div className="flex items-center gap-2">
                <img src="/deck/onmymind.png" alt="On My Mind" className="w-10 h-10 rounded object-cover shrink-0" />
                <div className="min-w-0">
                  <p className="text-base text-white font-medium">On My Mind</p>
                  <p className="text-base text-zinc-500">Diplo, Sidepiece · 334M streams</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function SlideSolution() {
  return (
    <>
    <TwoCol
      tag="The Solution"
      headlineNode={
        <span className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white leading-[1.15]">
          <DizeeWord className="text-[#FF2300]" />
          {' '}centralizes the modern music career.
        </span>
      }
      subline={undefined}
      right={
        <div className="flex flex-col items-center w-full">
          <MockCentralDizzy />
          <p className="mt-10 text-center text-sm text-zinc-500">Many tools → one system.</p>
        </div>
      }
    >
      <>
        <p className="mt-4 text-base sm:text-lg text-zinc-400">
          Into a single{' '}
          <span className="text-[#FF2300] font-semibold">operating system</span>
          {' '}and{' '}
          <span className="text-[#FF2300] font-semibold">source of truth.</span>
        </p>
        <hr className="mt-4 border-white/10" />
      </>
      <div className="mt-8 grid sm:grid-cols-1 gap-4">
        <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-5">
          <BarChart3 className="w-5 h-5 text-[#FF2300] mb-2" />
          <h3 className="text-base font-semibold text-white mb-1">Real-Time Clarity</h3>
          <p className="text-base text-zinc-400">Unified view of releases, shows, finances, team — in one place.</p>
        </div>
        <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-5">
          <Brain className="w-5 h-5 text-[#FF2300] mb-2" />
          <h3 className="text-base font-semibold text-white mb-1">Smarter Decisions</h3>
          <p className="text-base text-zinc-400">AI surfaces opportunities as they happen so you can act, not react.</p>
        </div>
        <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-5">
          <Target className="w-5 h-5 text-[#FF2300] mb-2" />
          <h3 className="text-base font-semibold text-white mb-1">End-to-End Management</h3>
          <p className="text-base text-zinc-400">Catalog, touring, team, projects — all connected.</p>
        </div>
      </div>
    </TwoCol>
    <p className="mt-10 text-xl sm:text-2xl lg:text-3xl font-medium tracking-tight text-zinc-300 italic text-center leading-snug">
      Anyone can now manage a modern music career end-to-end with{' '}
      <DizeeWord className="text-white" />
    </p>
    </>
  )
}

function SlidePricing() {
  const tiers = [
    {
      tier: 'Artist',
      audienceLabel: 'Independent artist',
      price: '$45',
      note: '/mo',
      annualPrice: '$459',
      annualNote: '/yr',
      annualMonthly: '$38.25',
      audience: 'Single user',
      features: ['Dashboard', 'Catalog & Release Management', 'Career Assistant', 'Schedule & Calendar', 'Tasks', '24/7 Sync'],
      plusFeatures: undefined as string[] | undefined,
      popular: false,
    },
    {
      tier: 'Artist Pro',
      audienceLabel: 'Industry professionals',
      price: '$75',
      note: '/mo',
      annualPrice: '$765',
      annualNote: '/yr',
      annualMonthly: '$63.75',
      audience: 'Single user',
      features: ['Dashboard', 'Catalog & Release Management', 'Career Assistant', 'Schedule & Calendar', 'Tasks', '24/7 Sync'],
      plusFeatures: ['Ticket Sales Tracker', 'Tour Manager', 'Dizee AI Assistant', 'Finance Tracking'],
      popular: true,
    },
    {
      tier: 'Enterprise',
      audienceLabel: 'Industry professionals',
      price: '$125',
      note: '/mo',
      annualPrice: '$1,275',
      annualNote: '/yr',
      annualMonthly: '$106.25',
      audience: '2+ users ($15 per additional seat)',
      features: ['Dashboard', 'Catalog & Release Management', 'Career Assistant', 'Schedule & Calendar', 'Tasks', '24/7 Sync', 'Tour Manager', 'Finance Tracking', 'Ticket Sales Tracker', 'Dizee AI Assistant'],
      plusFeatures: ['Teams', 'Songwriter Credits', 'Dedicated Customer Support'],
      popular: false,
    },
  ]
  return (
    <div>
      <SlideTag>Pricing</SlideTag>
      <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white">
        Simple subscription.{' '}
        <span className="text-[#FF2300]">Predictable revenue.</span>
      </h2>
      <div className="mt-10 flex flex-col lg:flex-row gap-6 lg:gap-8 items-stretch">
        {tiers.map((p, i) => (
          <div key={i} className="relative flex-1 flex flex-col min-h-0">
            {i === 1 && (
              <p className="text-[11px] font-semibold text-[#FF2300] uppercase tracking-widest mb-3">For industry professionals</p>
            )}
            {i === 0 && (
              <p className="text-[11px] font-semibold text-zinc-500 uppercase tracking-widest mb-3">For independent artists</p>
            )}
            {i === 2 && <p className="text-[11px] font-semibold uppercase tracking-widest mb-3 invisible" aria-hidden>For industry professionals</p>}
            <div className={`flex-1 flex flex-col min-h-0 rounded-xl border-2 p-5 ${(i === 1 || i === 2) ? 'border-[#FF2300]/40 bg-[#FF2300]/[0.06] ring-1 ring-[#FF2300]/20' : 'border-white/[0.06] bg-white/[0.02]'}`}>
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-sm font-semibold text-white">{p.tier}</h3>
                {p.popular && (
                  <span className="text-[9px] font-semibold text-[#FF2300] bg-[#FF2300]/10 rounded-full px-2 py-0.5">Popular</span>
                )}
              </div>
              {p.audienceLabel && <p className="text-base text-zinc-500 mb-2">{p.audienceLabel}</p>}
              <p className="text-2xl font-bold text-white mt-1">{p.price}<span className="text-[12px] font-normal text-zinc-500 ml-1">{p.note}</span></p>
              <p className="text-[13px] text-zinc-500 mt-0.5">Save 15% annually: <span className="text-zinc-400">{p.annualPrice}</span><span className="text-[11px] text-zinc-600 ml-0.5">{p.annualNote}</span> <span className="text-zinc-500">({p.annualMonthly}/mo)</span></p>
              <p className="text-base text-[#FF2300] font-medium mt-1">{p.audience}</p>
              <ul className="mt-4 space-y-1.5">
                {p.features.map((f, j) => (
                  <li key={j} className="flex items-center gap-2">
                    <Check className="w-3.5 h-3.5 text-zinc-600 shrink-0" />
                    <span className="text-base text-zinc-400">{f}</span>
                  </li>
                ))}
              </ul>
              {p.plusFeatures && p.plusFeatures.length > 0 && (
                <>
                  <div className="my-3 border-t border-white/[0.06]" />
                  <p className="text-[10px] font-semibold text-zinc-500 uppercase tracking-widest mb-2">Plus</p>
                  <ul className="space-y-1.5">
                    {p.plusFeatures.map((f, j) => (
                      <li key={j} className="flex items-center gap-2">
                        <Check className="w-3.5 h-3.5 text-[#FF2300] shrink-0" />
                        <span className="text-base text-zinc-300">{f}</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function SlideWhyDizeeWins() {
  const bullets = [
    'Built by a manager who\'s led artists to 2 Grammy nominations and responsible for 3B+ streams.',
    'CEO with proven industry credibility and owns an active Billboard-charting record label.',
    'Deep first-hand understanding of the full artist career lifecycle.',
    'Rare hybrid team of music insiders and modern product builders.',
    'CTO and engineering team have built diverse technology for iconic artists and across the industry — the right builders for this product.',
    'Experienced founders with growth-venture operating and capital-raising experience.',
    <span key="ai"><DizeeWord /> AI knowledge base informed by 15+ years of lived industry experience and real career data.</span>,
    'Strong global relationships across artists, agencies, promoters, and labels.',
    'Capital-efficient, agile, and lean operating team.',
    'Based in Los Angeles, the center of global music culture.',
  ]
  return (
    <TwoCol
      tagNode={<>Why <DizeeWord /> Wins</>}
      headlineNode={<>Why <DizeeWord className="text-[#FF2300]" /> wins.</>}
      right={
        <div className="flex flex-col items-center gap-4 w-full max-w-lg">
          <div className="relative w-full rounded-2xl overflow-hidden border border-white/[0.08] shadow-xl aspect-[16/10]">
            <img
              src={DECK_IMAGES.whyWins}
              alt="Why DIZEE wins"
              className="absolute inset-0 w-full h-full object-cover object-center"
            />
          </div>
        </div>
      }
    >
      <ul className="mt-8 space-y-4 max-w-3xl">
        {bullets.map((b, i) => (
          <li key={i} className="flex items-start gap-3">
            <Check className="w-5 h-5 text-[#FF2300] shrink-0 mt-0.5" />
            <span className="text-base text-zinc-300">{b}</span>
          </li>
        ))}
      </ul>
    </TwoCol>
  )
}

function SlideTeam() {
  const firstRow = TEAM_MEMBERS.slice(0, 3)
  const secondRow = TEAM_MEMBERS.slice(3, 5)
  return (
    <div>
      <SlideTag>Team</SlideTag>
      <h2 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tighter text-white">
        The team building this.
      </h2>
      <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch">
        {firstRow.map((t, i) => (
          <TeamCard key={i} t={t} />
        ))}
      </div>
      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-6 lg:max-w-[66.666%] mx-auto">
        {secondRow.map((t, i) => (
          <TeamCard key={i} t={t} />
        ))}
      </div>
    </div>
  )
}

function SlideEndCap() {
  return (
    <div className="flex flex-col justify-center items-center text-center py-8">
      <p className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-semibold tracking-tight text-white leading-snug max-w-4xl">
        <DizeeWord /> is building the modern operating system the music industry has been missing.
      </p>
      <p className="mt-8 text-lg sm:text-xl text-zinc-400 font-medium max-w-2xl">
        The team is assembled. The product is in motion.{' '}
        <span className="text-[#FF2300]">Timing is now.</span>
      </p>
    </div>
  )
}

function SlideClosing() {
  return (
    <div className="flex flex-col justify-center py-12">
      <div className="flex items-center gap-2.5 mb-8">
        <div className="w-6 h-6 bg-[#FF2300] rounded-sm flex-shrink-0" />
        <BrandLogo className="h-4 w-auto text-white" />
      </div>
      <div className="max-w-2xl">
        <p className="text-xl sm:text-2xl font-medium tracking-tight text-zinc-300 leading-snug">
          The operating system and source of truth for modern music careers — without changing how you work.
        </p>
      </div>
      <div className="mt-12 space-y-2">
        <p className="text-[14px] text-[#FF2300] font-medium">www.dizee.com</p>
      </div>
    </div>
  )
}

const SLIDES: Slide[] = [
  { title: 'Title', component: SlideTitle },
  { title: 'Mission', component: SlideMission },
  { title: 'Current State', component: SlideCurrentState },
  { title: 'Problem Statement', component: SlideProblemStatement },
  { title: 'Real Conversations', component: SlideRealConversations },
  { title: 'Solution', component: SlideSolution },
  { title: 'Problems we solve', component: SlideProblems },
  { title: 'Google Sheets', component: SlideSheets },
  { title: 'Teams & access', component: SlideAccess },
  { title: 'Schedule & Calendar', component: SlideCalendar },
  { title: 'Tasks', component: SlideTasks },
  { title: 'Summary', component: SlideSummary },
  { title: 'Pricing', component: SlidePricing },
  { title: 'Why DIZEE Wins', component: SlideWhyDizeeWins },
  { title: 'Team', component: SlideTeam },
  { title: 'End Cap', component: SlideEndCap },
  { title: 'Closing', component: SlideClosing },
]
